import { ELEMENT_TYPES } from 'components/RichTextEditorField/constants';
import EditorActions from 'components/RichTextEditorField/utils/EditorActions';
import { useMemo, useState } from 'react';
import { Descendant } from 'slate';
import { getHrefFromUrl, getIsArrayEmpty, safeParse } from 'utils';

const URL_REGEX =
  /(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}(\/[^\s]*)?(\?[^\s]*)?/g;
const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const DEFAULT_MAX = 999999; //temp fix

const createTextNode = (text: string, textProps: Record<string, unknown>) =>
  ({ text, ...textProps } as Descendant);

const splitTextWithLinks = (textNode: Descendant): Descendant[] => {
  if (!('text' in textNode)) return [textNode];

  const { text, ...textProps } = textNode as { text: string } & Record<
    string,
    unknown
  >;
  if (!text) return [textNode];

  const emailMatches = Array.from(text.matchAll(EMAIL_REGEX));
  const urlMatches = Array.from(text.matchAll(URL_REGEX)).filter(
    ({ index: urlStart }) =>
      !emailMatches.some((emailMatch) => {
        const emailStart = emailMatch.index;

        return (
          urlStart >= emailStart &&
          urlStart < emailStart + emailMatch[0]?.length
        );
      }),
  );

  const matches = [
    ...emailMatches.map((match) => ({
      match,
      type: ELEMENT_TYPES.EMAIL,
    })),
    ...urlMatches.map((match) => ({
      match,
      type: ELEMENT_TYPES.LINK,
    })),
  ];

  if (getIsArrayEmpty(matches)) return [textNode];

  const result: Descendant[] = [];
  let lastIndex = 0;

  for (const { match, type } of matches) {
    const value = match[0];
    const start = match.index ?? 0;

    if (start > lastIndex) {
      result.push(createTextNode(text.slice(lastIndex, start), textProps));
    }

    result.push(
      type === ELEMENT_TYPES.EMAIL
        ? EditorActions.createEmailNode(value, textProps)
        : EditorActions.createLinkNode(getHrefFromUrl(value), value, textProps),
    );
    lastIndex = start + value.length;
  }

  if (lastIndex < text.length) {
    result.push(createTextNode(text.slice(lastIndex), textProps));
  }

  return result;
};

const linkifyDescendants = (nodes: Descendant[]): Descendant[] =>
  nodes.flatMap((node) => {
    if ('text' in node) return splitTextWithLinks(node);

    if (
      node.type === ELEMENT_TYPES.LINK ||
      node.type === ELEMENT_TYPES.EMAIL ||
      !('children' in node)
    )
      return [node];

    return [
      {
        ...node,
        children: linkifyDescendants(node.children as Descendant[]),
      } as Descendant,
    ];
  });

const useDescription = (children: string, maxLengthPrp?: number) => {
  const [showMore, setShowMore] = useState(false);
  const maxLength = maxLengthPrp || DEFAULT_MAX;

  const parsedDescription = useMemo(() => {
    if (!children) return null;

    const pd = safeParse(children);

    if (Array.isArray(pd)) {
      return linkifyDescendants(pd.filter((d) => 'type' in d));
    }

    return null;
  }, [children]);

  const cutDescription = useMemo(() => {
    if (!parsedDescription) return null;

    let accLength = 0;

    const cutChildren = (elements: Descendant[]): Descendant[] => {
      return elements.reduce((acc, el, ind) => {
        if (accLength >= maxLength) {
          setShowMore(true);
          return acc;
        }

        if ('text' in el) {
          accLength += el.text.length;
          if (accLength < maxLength) {
            return [...acc, el];
          } else {
            return [
              ...acc,
              {
                text: el.text.slice(
                  0,
                  Math.max(0, maxLength - (accLength - el.text.length)),
                ),
              },
            ];
          }
        }

        if (ind !== 0 && el.type !== ELEMENT_TYPES.INDENT) {
          accLength += 20;
        } else {
          accLength += 4;
        }

        if (accLength >= maxLength) return acc;

        const newEl = {
          ...el,
          children: cutChildren(el.children),
        } as Descendant;
        return [...acc, newEl];
      }, [] as Descendant[]);
    };

    return cutChildren(parsedDescription);
  }, [maxLength, parsedDescription]);

  return [cutDescription, parsedDescription, showMore];
};

export default useDescription;
